import { useEffect, useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronDown,
  X,
} from "lucide-react";
import toast from "react-hot-toast";

import useContactForm from "../../services/LandingPage/useContactForm";

const initialFormData = {
  name: "",
  email: "",
  phone: "",
  restaurantName: "",
  latitude: "",
  longitude: "",
};

export default function ContactDemo({ isOpen, onClose }) {
  const {
    submitRestaurantBasicInfo,
    getOrganizationTypes,
    loading,
  } = useContactForm();

  const [formData, setFormData] = useState(initialFormData);
  const [organizationTypes, setOrganizationTypes] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    setIsSubmitted(false);
    setShowDropdown(false);

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

useEffect(() => {
  if (!isOpen) return;

  let isMounted = true;

  const loadTypes = async () => {
    try {
      const response = await getOrganizationTypes();

      if (!isMounted) return;

      if (response?.success) {
        setOrganizationTypes(
          Array.isArray(response?.data) ? response.data : [],
        );
      } else {
        setOrganizationTypes([]);
      }
    } catch (error) {
      console.error("loadTypes error:", error);

      if (isMounted) {
        setOrganizationTypes([]);
      }
    }
  };

  loadTypes();

  return () => {
    isMounted = false;
  };
}, [isOpen, getOrganizationTypes]);

  const updateFormField = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleClose = () => {
    if (loading) return;

    setFormData(initialFormData);
    setShowDropdown(false);
    setIsSubmitted(false);
    onClose();
  };

  const handleBookDemo = async (event) => {
    event.preventDefault();

    if (loading) return;

    const cleanData = {
      name: String(formData.name || "").trim(),
      phone: String(formData.phone || "").trim(),
      email: String(formData.email || "").trim(),
      restaurantName: String(
        formData.restaurantName || "",
      ).trim(),
      latitude:
        String(formData.latitude || "").trim() ||
        "28.624711238077882",
      longitude:
        String(formData.longitude || "").trim() ||
        "77.3717769111169",
    };

    if (
      !cleanData.name ||
      !cleanData.phone ||
      !cleanData.email ||
      !cleanData.restaurantName
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (!/^[A-Za-z\s]+$/.test(cleanData.name)) {
      toast.error("Name should contain only letters.");
      return;
    }

    if (!/^\d{10}$/.test(cleanData.phone)) {
      toast.error(
        "Phone number must contain exactly 10 digits.",
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(cleanData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      const response =
        await submitRestaurantBasicInfo(cleanData);

      if (response?.success) {
        setFormData(initialFormData);
        setShowDropdown(false);
        setIsSubmitted(true);

        setTimeout(() => {
          setIsSubmitted(false);
          onClose();
        }, 3000);

        return;
      }

      toast.error(
        response?.message || "Unable to submit details.",
      );
    } catch (error) {
      console.error("handleBookDemo error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      );
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        className="relative h-full max-h-[100dvh] w-full max-w-2xl overflow-y-auto rounded-none bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[30px]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="relative bg-gradient-to-r from-[#01C37D] to-[#00AE70] px-5 py-6 text-white sm:px-8 sm:py-7">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            aria-label="Close demo form"
            className="absolute right-3 top-3 rounded-full bg-white/20 p-2 transition hover:bg-white/30 disabled:cursor-not-allowed disabled:opacity-50 sm:right-5 sm:top-5"
          >
            <X size={18} />
          </button>

          <h2 className="text-2xl font-bold sm:text-3xl">
            {isSubmitted
              ? "Thank You!"
              : "Book a Free Demo"}
          </h2>

          <p className="mt-2 text-sm leading-6 text-green-50 sm:text-base">
            {isSubmitted
              ? "Your details have been submitted successfully."
              : "Fill in your details and our team will get in touch shortly."}
          </p>
        </div>

        {isSubmitted ? (
          <div className="flex min-h-[390px] flex-col items-center justify-center px-5 py-10 text-center sm:px-8">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <Check
                className="h-10 w-10 text-[#01C37D]"
                strokeWidth={3}
              />
            </div>

            <h3 className="mt-6 text-2xl font-bold text-slate-900">
              Details Submitted Successfully!
            </h3>

            <p className="mt-3 max-w-md text-base leading-7 text-slate-600">
              Thank you for showing interest in Anlook.
            </p>

            <p className="mt-2 max-w-md text-lg font-semibold text-[#01C37D]">
              Our team will contact you within 24 hours.
            </p>

            <button
              type="button"
              onClick={handleClose}
              className="mt-8 h-12 min-w-[180px] rounded-xl bg-[#01C37D] px-6 font-semibold text-white shadow-md transition hover:bg-[#00AE70]"
            >
              Done
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleBookDemo}
            className="space-y-5 p-5 sm:space-y-6 sm:p-8"
          >
            <div className="grid grid-cols-1 gap-x-5 gap-y-6 lg:grid-cols-2">
              <div className="relative">
                <input
                  id="name"
                  type="text"
                  placeholder=" "
                  value={formData.name}
                  disabled={loading}
                  onChange={(event) => {
                    const value = event.target.value;

                    if (/^[A-Za-z\s]*$/.test(value)) {
                      updateFormField("name", value);
                    }
                  }}
                  className="peer h-12 w-full rounded-xl border border-slate-300 px-4 pt-5 outline-none transition focus:border-[#01C37D] focus:ring-4 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-slate-100 sm:h-14"
                />

                <label
                  htmlFor="name"
                  className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-xs peer-focus:text-[#01C37D] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs"
                >
                  Name
                  <span className="text-red-500"> *</span>
                </label>
              </div>

              <div className="relative">
                <input
                  id="phone"
                  type="tel"
                  placeholder=" "
                  value={formData.phone}
                  maxLength={10}
                  inputMode="numeric"
                  disabled={loading}
                  onChange={(event) => {
                    const value = event.target.value.replace(
                      /\D/g,
                      "",
                    );

                    if (value.length <= 10) {
                      updateFormField("phone", value);
                    }
                  }}
                  className="peer h-12 w-full rounded-xl border border-slate-300 px-4 pt-5 outline-none transition focus:border-[#01C37D] focus:ring-4 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-slate-100 sm:h-14"
                />

                <label
                  htmlFor="phone"
                  className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-xs peer-focus:text-[#01C37D] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs"
                >
                  Phone Number
                  <span className="text-red-500"> *</span>
                </label>
              </div>

              <div className="relative">
                <input
                  id="email"
                  type="email"
                  placeholder=" "
                  value={formData.email}
                  disabled={loading}
                  onChange={(event) =>
                    updateFormField(
                      "email",
                      event.target.value,
                    )
                  }
                  className="peer h-12 w-full rounded-xl border border-slate-300 px-4 pt-5 outline-none transition focus:border-[#01C37D] focus:ring-4 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-slate-100 sm:h-14"
                />

                <label
                  htmlFor="email"
                  className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500 transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-base peer-focus:-top-2 peer-focus:text-xs peer-focus:text-[#01C37D] peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-xs"
                >
                  Email
                  <span className="text-red-500"> *</span>
                </label>
              </div>

              <div className="relative">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() =>
                    setShowDropdown(
                      (previous) => !previous,
                    )
                  }
                  className="flex h-12 w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-4 text-left outline-none transition focus:border-[#01C37D] focus:ring-4 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-slate-100 sm:h-14"
                >
                  <span
                    className={
                      formData.restaurantName
                        ? "text-slate-800"
                        : "text-slate-400"
                    }
                  >
                    {formData.restaurantName ||
                      "Select Business Type"}
                  </span>

                  <ChevronDown
                    size={18}
                    className={`transition-transform ${
                      showDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <label className="absolute left-4 -top-2 bg-white px-1 text-xs text-slate-500">
                  Business Type
                  <span className="text-red-500"> *</span>
                </label>

                {showDropdown && (
                  <div className="absolute left-0 top-[calc(100%+6px)] z-50 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
                    {organizationTypes.length === 0 ? (
                      <p className="px-4 py-3 text-sm text-slate-500">
                        No business types found
                      </p>
                    ) : (
                      organizationTypes.map(
                        (item, index) => {
                          const itemId =
                            item?.id ||
                            item?.pk_id ||
                            index;

                          const itemName =
                            item?.restro_type ||
                            item?.organization_type ||
                            item?.name ||
                            "";

                          return (
                            <button
                              key={`${itemId}-${itemName}`}
                              type="button"
                              onClick={() => {
                                updateFormField(
                                  "restaurantName",
                                  itemName,
                                );
                                setShowDropdown(false);
                              }}
                              className="block w-full px-4 py-3 text-left text-sm text-slate-700 transition hover:bg-[#01C37D]/10 hover:text-[#008B59]"
                            >
                              {itemName}
                            </button>
                          );
                        },
                      )
                    )}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#01C37D] to-[#00AE70] text-base font-semibold text-white shadow-lg transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60 sm:h-14 sm:text-lg"
            >
              {loading ? (
                <>
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Details
                  <ArrowRight size={20} />
                </>
              )}
            </button>

            <p className="text-center text-sm text-slate-500">
              Our team will contact you within
              <span className="font-semibold text-[#01C37D]">
                {" "}
                24 hours.
              </span>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}